/**
 * Revision History — writing-process visibility for Google Docs & Slides
 *
 * A single web-accessible-resource entry exposes `js/*` and `images/*` to a match list
 * that ends in `<all_urls>`, with no `use_dynamic_url` — so every bundled script and
 * image is fetchable from any page at a fixed URL. The `black-logo-*.png` set is
 * vendor-named and, being images, loads through `probeImage` with no CORS grant. The
 * `js/kixBridge.js` script (a Google Docs "Kix" editor bridge) is a second, distinctly
 * named path that corroborates the identification.
 *
 * Manifest verified against version 1.10.9.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'dlepebghjlnddgihakmnpoiifjjpmomh';

export const revisionHistory: ExtensionDefinition = {
  id: ID,
  name: 'Revision History: Writing Process Visibility for Google Docs & Slides',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  notes:
    'Exposes js/* and images/* to a match list ending in <all_urls> with no dynamic URL. ' +
    'The vendor-named black-logo images are conclusive and load without a CORS grant.',
  signals: [
    {
      id: 'war-black-logo-128',
      kind: 'web-accessible-resource',
      description:
        'Load images/black-logo-128.png via <img>. A vendor-named icon exposed to every ' +
        'origin; needs no CORS grant, so it survives a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'images/black-logo-128.png'),
    },
    {
      id: 'war-black-logo-svg',
      kind: 'web-accessible-resource',
      description:
        'Load images/black-logo.svg via <img> — a second vendor-named image path that ' +
        'corroborates the identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'images/black-logo.svg'),
    },
    {
      id: 'war-kix-bridge-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch js/kixBridge.js — a distinctly named Google Docs editor bridge script, ' +
        'independent of the image paths.',
      weight: 0.5,
      run: () => probeFetch(ID, 'js/kixBridge.js'),
    },
  ],
};
